import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  IdCard, Upload, Loader2, X, Sparkles, Download, Printer,
  Wand2, ScanLine, ArrowLeft, ArrowRight, Camera, RefreshCcw,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────
//  Card types
// ─────────────────────────────────────────────────────────────

const CARD_TYPES = [
  { key: "aadhaar",  label: "Aadhaar" },
  { key: "pan",      label: "PAN" },
  { key: "voter",    label: "Voter ID" },
  { key: "dl",       label: "Driving Licence" },
  { key: "rc",       label: "RC Book" },
  { key: "ayushman", label: "Ayushman Card" },
  { key: "abha",     label: "ABHA / Health ID" },
  { key: "ration",   label: "Ration Card" },
  { key: "eshram",   label: "e-Shram" },
  { key: "other",    label: "Other ID / Card" },
];

const A4_W_MM = 210;
const A4_H_MM = 297;
const CARD_W_MM = 85.60;
const CARD_H_MM = 53.98;
const CHARGE_INR = 2;

// ─────────────────────────────────────────────────────────────
//  A4 preview (SVG)
// ─────────────────────────────────────────────────────────────

function A4Preview({ job, orientation, frontSrc, backSrc }) {
  // Compute the exact same layout as compose_a4:
  const sides = [];
  if (frontSrc) sides.push({ name: "front", src: frontSrc });
  if (backSrc) sides.push({ name: "back", src: backSrc });

  const positions = useMemo(() => {
    const top = 20, gap = 8;
    if (sides.length === 1) {
      return [{ side: sides[0].name, x_mm: (A4_W_MM - CARD_W_MM) / 2, y_mm: top }];
    }
    if (orientation === "portrait") {
      const totalH = 2 * CARD_H_MM + gap;
      const startY = Math.max(top, (A4_H_MM - totalH) / 2);
      const x = (A4_W_MM - CARD_W_MM) / 2;
      return sides.map((s, i) => ({ side: s.name, x_mm: x, y_mm: startY + i * (CARD_H_MM + gap) }));
    }
    const totalW = 2 * CARD_W_MM + gap;
    const startX = (A4_W_MM - totalW) / 2;
    return sides.map((s, i) => ({ side: s.name, x_mm: startX + i * (CARD_W_MM + gap), y_mm: top }));
  }, [sides.length, orientation]);

  const scale = 520 / Math.max(A4_W_MM, A4_H_MM);
  const w = A4_W_MM * scale, h = A4_H_MM * scale;
  return (
    <div className="flex flex-col items-center gap-2" data-testid="a4-preview">
      <div className="text-xs text-muted-foreground">A4 · {A4_W_MM}×{A4_H_MM} mm · cards at exact {CARD_W_MM}×{CARD_H_MM} mm</div>
      <svg width={w} height={h} className="bg-white shadow-lg border border-slate-200 rounded-sm">
        {sides.map((s, i) => (
          <image key={s.name}
            href={s.src}
            x={positions[i].x_mm * scale}
            y={positions[i].y_mm * scale}
            width={CARD_W_MM * scale}
            height={CARD_H_MM * scale}
            preserveAspectRatio="xMidYMid slice"
          />
        ))}
        {sides.map((s, i) => (
          <rect key={"r" + i}
            x={positions[i].x_mm * scale}
            y={positions[i].y_mm * scale}
            width={CARD_W_MM * scale}
            height={CARD_H_MM * scale}
            fill="none" stroke="#94a3b8" strokeDasharray="3 2" strokeWidth="0.5"
          />
        ))}
      </svg>
      <div className="text-xs text-muted-foreground">
        {sides.length === 2 ? "Front + Back placed side-by-side" : "Single side centred"}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Manual crop dialog (4-corner draggable quad)
// ─────────────────────────────────────────────────────────────

function ManualCropDialog({ open, onOpenChange, job, side, onDone }) {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageUrl, setPageUrl] = useState(null);
  const [corners, setCorners] = useState([[0.1, 0.1], [0.9, 0.1], [0.9, 0.9], [0.1, 0.9]]);
  const [dragIdx, setDragIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const svgRef = useRef(null);

  useEffect(() => {
    if (!open || !job) return;
    (async () => {
      try {
        const resp = await api.get(`/id-card-a4/${job.id}/page/${pageIndex}`, { responseType: "blob" });
        setPageUrl(URL.createObjectURL(resp.data));
      } catch (e) {
        toast.error(formatApiErrorDetail(e) || "Page preview failed");
      }
    })();
  }, [open, job?.id, pageIndex]);

  const onMove = (e) => {
    if (dragIdx < 0 || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - box.left) / box.width));
    const y = Math.max(0, Math.min(1, (e.clientY - box.top) / box.height));
    setCorners((c) => c.map((p, i) => (i === dragIdx ? [x, y] : p)));
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/id-card-a4/${job.id}/manual-crop`, {
        side, page_index: pageIndex, corners,
      });
      toast.success(`${side === "front" ? "Front" : "Back"} saved`);
      onDone && onDone();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Manual crop failed");
    } finally { setBusy(false); }
  };

  if (!job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Adjust {side === "front" ? "Front" : "Back"} side</DialogTitle>
          <DialogDescription>Drag the 4 corners to enclose the card. Perspective is corrected automatically.</DialogDescription>
        </DialogHeader>
        {job.page_count > 1 && (
          <div className="flex items-center gap-2 text-sm">
            <span>Page:</span>
            {Array.from({ length: job.page_count }).map((_, i) => (
              <Button key={i} size="sm" variant={i === pageIndex ? "default" : "outline"} onClick={() => setPageIndex(i)} data-testid={`manual-page-${i}`}>
                {i + 1}
              </Button>
            ))}
          </div>
        )}
        {pageUrl && (
          <div className="relative bg-slate-900 rounded overflow-hidden select-none" style={{ aspectRatio: "1 / 1.414" }}>
            <img src={pageUrl} alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
            <svg ref={svgRef} viewBox="0 0 1 1" className="absolute inset-0 w-full h-full" onMouseMove={onMove} onMouseUp={() => setDragIdx(-1)} onMouseLeave={() => setDragIdx(-1)}
              preserveAspectRatio="none" data-testid="manual-quad">
              <polygon points={corners.map((c) => c.join(",")).join(" ")}
                fill="rgba(59,130,246,0.15)" stroke="#3b82f6" strokeWidth="0.005" vectorEffect="non-scaling-stroke" />
              {corners.map(([x, y], i) => (
                <circle key={i} cx={x} cy={y} r="0.02" fill="#3b82f6" stroke="white" strokeWidth="0.005"
                  onMouseDown={() => setDragIdx(i)} style={{ cursor: "grab" }}
                  vectorEffect="non-scaling-stroke" data-testid={`manual-corner-${i}`} />
              ))}
            </svg>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" onClick={() => setCorners((c) => [c[3], c[0], c[1], c[2]])} data-testid="manual-rotate">
            Rotate 90°
          </Button>
          <Button variant="outline" onClick={() => setCorners([[0.02, 0.02], [0.98, 0.02], [0.98, 0.98], [0.02, 0.98]])} data-testid="manual-reset">
            Reset
          </Button>
          <Button onClick={submit} disabled={busy} data-testid="manual-crop-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}
            Save side
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────

export default function IdCardA4() {
  const [cardType, setCardType] = useState("aadhaar");
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [orientation, setOrientation] = useState("landscape");
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSide, setManualSide] = useState("front");
  const [previewSrcs, setPreviewSrcs] = useState({ front: null, back: null });
  const [paid, setPaid] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [printerId, setPrinterId] = useState("");
  const [printing, setPrinting] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/printers");
        setPrinters(data);
        const d = data.find((p) => p.is_default) || data[0];
        if (d) setPrinterId(d.id);
      } catch { /* no printers yet */ }
    })();
  }, []);

  // Detect encrypted PDF on file pick so we only show the password field when needed.
  useEffect(() => {
    if (!file) { setNeedsPassword(false); return; }
    if (!/\.pdf$/i.test(file.name)) { setNeedsPassword(false); return; }
    (async () => {
      try {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/pvc/pdf-info", fd);
        setNeedsPassword(!!data.encrypted);
      } catch { setNeedsPassword(false); }
    })();
  }, [file]);

  const refreshPreviews = async (jobDoc) => {
    if (!jobDoc) return;
    const next = { front: null, back: null };
    for (const side of ["front", "back"]) {
      if (jobDoc[side]) {
        try {
          const r = await api.get(`/id-card-a4/${jobDoc.id}/preview/${side}?_=${Date.now()}`, { responseType: "blob" });
          next[side] = URL.createObjectURL(r.data);
        } catch { /* ignore */ }
      }
    }
    setPreviewSrcs(next);
  };

  const upload = async () => {
    if (!file) return toast.error("Pick a PDF or image");
    setBusy(true); setPaid(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("card_type", cardType);
      if (password) fd.append("password", password);
      const { data } = await api.post("/id-card-a4/upload", fd);
      setJob(data);
      await refreshPreviews(data);
      if (data.manual_needed && cardType === "other") {
        toast.info("Please crop Front and Back manually");
        setManualSide("front");
        setManualOpen(true);
      } else if (data.manual_needed) {
        toast.warning("Auto-detection couldn't find both sides — please crop manually");
      } else {
        toast.success(`Detected · confidence ${(data.confidence * 100).toFixed(0)}%`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Upload failed");
    } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!job) return;
    setBusy(true);
    try {
      // sync orientation choice
      const fd = new FormData();
      fd.append("orientation", orientation);
      await api.post(`/id-card-a4/${job.id}/orientation`, fd);
      const { data } = await api.post(`/id-card-a4/${job.id}/generate`);
      setPaid(!!data.paid);
      if (data.paid) toast.success(`Charged ₹${CHARGE_INR} · A4 ready`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Generate failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    try {
      const resp = await api.get(`/id-card-a4/${job.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `MaaPrint_${(job.card_type || "card").toUpperCase()}_A4.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Download failed"); }
  };

  const directPrint = async () => {
    if (!job || !paid) return;
    try {
      const resp = await api.get(`/id-card-a4/${job.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      // Open the PDF in a hidden iframe so the browser's native print dialog
      // fires without leaving MaaPrint. Cleaned up after print or 30s.
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      iframe.src = url;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
          catch (e) { toast.error("Browser blocked the print dialog — try Download instead"); }
        }, 300);
      };
      setTimeout(() => {
        URL.revokeObjectURL(url);
        try { document.body.removeChild(iframe); } catch { /* already gone */ }
      }, 30000);
    } catch (e) { toast.error(formatApiErrorDetail(e) || "Direct print failed"); }
  };

  const printNow = async () => {
    if (!job) return;
    setPrinting(true);
    try {
      const fd = new FormData();
      if (printerId) fd.append("printer_id", printerId);
      const { data } = await api.post(`/id-card-a4/${job.id}/print-now`, fd);
      toast.success(`Sent to ${data.printer || "printer"} · ${data.status}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Print failed");
    } finally { setPrinting(false); }
  };

  const startOver = () => {
    setJob(null); setFile(null); setPassword(""); setPaid(false);
    setPreviewSrcs({ front: null, back: null });
  };

  const bothReady = job && job.front && job.back;
  const anyReady = job && (job.front || job.back);
  const confidence = job?.confidence ?? 0;
  const confidenceLabel = confidence >= 0.7 ? "High" : confidence >= 0.4 ? "Medium" : "Low";
  const confidenceTone = confidence >= 0.7 ? "bg-emerald-100 text-emerald-800" : confidence >= 0.4 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800";

  return (
    <div className="space-y-6 pb-16" data-testid="id-card-a4-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
          <IdCard className="h-7 w-7 text-blue-600" /> ID Card → A4 Print
        </h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Print any ID card (Aadhaar, PAN, Voter, DL, RC, Ayushman, ABHA, Ration, e-Shram or Other) on A4 at exact
          {" "}{CARD_W_MM}×{CARD_H_MM} mm. AI auto-crops Front + Back and places them side-by-side.
          Every successful A4 costs ₹{CHARGE_INR} from your wallet.
        </p>
      </div>

      {!job && (
        <Card className="p-5 space-y-4" data-testid="upload-panel">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Document type</Label>
              <Select value={cardType} onValueChange={setCardType}>
                <SelectTrigger data-testid="card-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CARD_TYPES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {needsPassword && (
              <div className="space-y-1" data-testid="pdf-password-field">
                <Label>PDF password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="This PDF is password-protected" data-testid="pdf-password" />
              </div>
            )}
          </div>
          <label
            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition p-10 text-center ${drag ? "bg-blue-50 border-blue-500" : "bg-slate-50 border-slate-300 hover:bg-slate-100"}`}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) setFile(f); }}
            data-testid="upload-drop"
          >
            <Upload className="h-10 w-10 mb-2 text-slate-500" />
            <div className="text-sm font-medium">{file ? file.name : "Tap or drop PDF / JPG / PNG here"}</div>
            <div className="text-xs text-muted-foreground mt-1">Password-protected PDFs supported · max 15 MB</div>
            <input ref={inputRef} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setFile(f); e.target.value = ""; }}
            />
          </label>
          {file && (
            <div className="flex items-center gap-3 text-sm">
              <span className="font-medium">{file.name}</span>
              <span className="text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</span>
              <Button variant="ghost" size="icon" onClick={() => setFile(null)} data-testid="upload-remove">
                <X className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          )}
          <Button onClick={upload} disabled={!file || busy} className="w-full md:w-auto" data-testid="upload-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ScanLine className="h-4 w-4 mr-2" />}
            Upload &amp; detect
          </Button>
        </Card>
      )}

      {job && (
        <div className="grid gap-6 lg:grid-cols-[1fr_520px]">
          {/* left: sides + controls */}
          <div className="space-y-4">
            <Card className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={confidenceTone} data-testid="confidence-badge">
                  AI confidence: {confidenceLabel} ({(confidence * 100).toFixed(0)}%)
                </Badge>
                <Button size="sm" variant="ghost" onClick={startOver} data-testid="start-over">
                  <RefreshCcw className="h-4 w-4 mr-1" /> Start over
                </Button>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                {["front", "back"].map((side) => (
                  <div key={side} className="space-y-2" data-testid={`side-${side}`}>
                    <div className="text-sm font-medium capitalize">{side}</div>
                    {previewSrcs[side] ? (
                      <img src={previewSrcs[side]} alt={side} className="w-full max-w-[320px] aspect-[85.6/54] object-cover rounded border" />
                    ) : (
                      <div className="w-full max-w-[320px] aspect-[85.6/54] rounded border-2 border-dashed border-slate-300 flex items-center justify-center text-xs text-muted-foreground">
                        Not detected — click Adjust
                      </div>
                    )}
                    <Button size="sm" variant="outline" onClick={() => { setManualSide(side); setManualOpen(true); }} data-testid={`adjust-${side}`}>
                      <Wand2 className="h-4 w-4 mr-1" /> Adjust {side}
                    </Button>
                  </div>
                ))}
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>A4 layout</Label>
                  <Select value={orientation} onValueChange={(v) => { setOrientation(v); setPaid(false); }}>
                    <SelectTrigger data-testid="orientation-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="landscape">Side-by-side (recommended)</SelectItem>
                      <SelectItem value="portrait">Stacked (top &amp; bottom)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Printer</Label>
                  <Select value={printerId} onValueChange={setPrinterId}>
                    <SelectTrigger data-testid="printer-select">
                      <SelectValue placeholder={printers.length ? "Select printer" : "No printer connected"} />
                    </SelectTrigger>
                    <SelectContent>
                      {printers.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </Card>

            <Card className="p-4 flex flex-wrap items-center gap-3 sticky bottom-4 bg-white/95 backdrop-blur shadow-lg">
              <div className="text-sm">
                <div className="font-semibold">Charge: ₹{CHARGE_INR}</div>
                <div className="text-xs text-muted-foreground">Debited from your wallet on successful generation</div>
              </div>
              <div className="flex-1" />
              <Button variant="outline" onClick={generate} disabled={!anyReady || busy} data-testid="generate-btn">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                Generate A4 (₹{CHARGE_INR})
              </Button>
              <Button variant="secondary" onClick={downloadPdf} disabled={!paid} data-testid="download-btn">
                <Download className="h-4 w-4 mr-1" /> Download PDF
              </Button>
              <Button variant="secondary" onClick={directPrint} disabled={!paid} data-testid="direct-print-btn">
                <Printer className="h-4 w-4 mr-1" /> Direct Print
              </Button>
              <Button onClick={printNow} disabled={!paid || printing} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="print-btn">
                {printing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Printer className="h-4 w-4 mr-1" />}
                Print via Connector
              </Button>
            </Card>
          </div>

          {/* right: A4 live preview */}
          <Card className="p-4">
            <A4Preview job={job} orientation={orientation}
              frontSrc={previewSrcs.front} backSrc={previewSrcs.back} />
          </Card>
        </div>
      )}

      <ManualCropDialog
        open={manualOpen}
        onOpenChange={setManualOpen}
        job={job}
        side={manualSide}
        onDone={async () => {
          // Reload previews after the manual crop stored a fresh side JPEG.
          const patchedJob = { ...job, [manualSide]: { preview_url: `/api/id-card-a4/${job.id}/preview/${manualSide}` }, manual_needed: false };
          setJob(patchedJob);
          setPaid(false);
          await refreshPreviews(patchedJob);
        }}
      />
    </div>
  );
}
